/**
 * The LOS-vs-document disagreement workflow for one loan on one tape
 * (funding-tape-prep.md §3): `GET /fundings/tapes/{tapeId}/reconcile/{dealId}`
 * attaches an `agreement` to every column with a document source; the
 * reviewer resolves each disagreement as `los` / `document` / `manual` via
 * `POST …/resolve`.
 *
 * A `document` resolution never writes the loan — the LOS is corrected on the
 * loan itself, and on the next reconcile the column re-reads and flips to plain
 * `agree` (source LOS, documents agree). Rendered as a three-column grid per
 * field: LOS · Documents (expandable to every observation, lower rank = higher
 * precedence) · Status.
 */
import { useCallback, useEffect, useState } from "react";

import CloseRounded from "@mui/icons-material/CloseRounded";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

import { Muted, Pill } from "../../components/closingReadiness/ReadinessBits";
import { agreementMeta } from "../../components/closingReadiness/closingReadinessUtils";
import InputWrapper from "../../components/inputs/shared/InputWrapper";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { RESOLUTIONS, fundingsApi } from "../../api/fundingsApi";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { slate, status, steel } from "../../theme/colors";
import { C, MONO, loanHref } from "./fundingsUi";

const fmtVal = (v) => {
  if (v == null || v === "") return "—";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(v > 100 ? 2 : 4).replace(/\.?0+$/, "");
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
};

const ColumnRow = ({ col, tapeId, dealId, onResolved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const a = col?.agreement;
  const am = agreementMeta(a?.status);
  const [open, setOpen] = useState(false);
  const [manual, setManual] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(null);
  const needsAction = a && ["disagree", "los_only", "doc_only"].includes(a.status);

  const resolve = async (resolution) => {
    setBusy(resolution);
    try {
      const res = await fundingsApi.resolve(tapeId, dealId, {
        field: col.field,
        resolution,
        value: resolution === RESOLUTIONS.MANUAL ? manual : undefined,
        note: note || undefined,
      });
      enqueueSnackbar(`${col.label || col.field}: resolved as ${resolution}.`, { variant: "success" });
      setOpen(false);
      if (typeof onResolved === "function") onResolved(res);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.detail || "Could not resolve.", { variant: "error" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ borderBottom: `1px solid ${C.lineSoft}`, py: 1.25 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr) 110px", columnGap: 1.5, alignItems: "center" }}>
        <Box sx={{ minWidth: 0 }}>
          <Txt sx={{ fontSize: 12.5, fontWeight: 700, color: C.ink, display: "block" }}>
            {col.label || col.field}
            {col.required && (
              <Box component="span" sx={{ color: status.error.solid, ml: 0.4 }}>
                *
              </Box>
            )}
          </Txt>
          <Muted sx={{ fontSize: 10.5, fontFamily: MONO, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {col.documentSource || col.source || col.field}
          </Muted>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Txt sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 600, color: col.missing ? status.error.fg : C.ink, display: "block" }}>
            {fmtVal(a ? a.losValue : col.value)}
          </Txt>
          <Muted sx={{ fontSize: 10.5, display: "block" }}>{a ? "LOS" : col.source || ""}</Muted>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {a ? (
            <>
              <Txt sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 600, color: C.ink, display: "block" }}>
                {fmtVal(a.documentValue)}
              </Txt>
              <Flx ac g={0.5}>
                <Muted sx={{ fontSize: 10.5 }}>{a.documentSource || "no document read"}</Muted>
                {Array.isArray(a.observations) && a.observations.length > 0 && (
                  <KeyboardArrowDownRounded
                    onClick={() => setOpen((o) => !o)}
                    sx={{ fontSize: 15, color: slate[400], cursor: "pointer", transform: open ? "rotate(180deg)" : "none" }}
                  />
                )}
              </Flx>
            </>
          ) : (
            <Muted sx={{ fontSize: 11.5 }}>no document source</Muted>
          )}
        </Box>
        <Flx ac jc>
          {a ? <Pill label={am.label} c={am.c} bg={am.bg} size="xs" /> : <Pill label="LOS" c={slate[500]} bg={slate[100]} size="xs" />}
        </Flx>
      </Box>

      {open && Array.isArray(a?.observations) && (
        <Box sx={{ mt: 0.75, ml: 1, pl: 1.25, borderLeft: `2px solid ${C.lineSoft}` }}>
          {a.observations.map((o, i) => (
            <Flx ac g={1} key={i} sx={{ py: 0.25 }}>
              <Pill label={`rank ${o.rank}`} c={o.rank === 0 ? steel.main : slate[500]} bg={o.rank === 0 ? steel.tint : slate[100]} size="xs" />
              <Txt sx={{ fontSize: 12, fontWeight: 600, color: C.ink }}>{o.docType}</Txt>
              <Txt sx={{ fontFamily: MONO, fontSize: 12, color: C.ink }}>{fmtVal(o.value)}</Txt>
              {o.extractedAt && <Muted sx={{ fontSize: 10.5 }}>{formatDateTime(o.extractedAt, "date")}</Muted>}
            </Flx>
          ))}
        </Box>
      )}

      {a?.status === "resolved" && (
        <Muted sx={{ display: "block", mt: 0.5, fontSize: 11.5 }}>
          Resolved as <b>{a.resolution}</b>
          {a.resolvedBy ? ` by ${a.resolvedBy}` : ""}
          {a.resolvedAt ? ` · ${formatDateTime(a.resolvedAt, "datetime")}` : ""}
          {a.note ? ` · ${a.note}` : ""}
          {a.agreesAfterResolution === false ? " · LOS still differs — correct the loan and re-reconcile" : ""}
        </Muted>
      )}

      {needsAction && (
        <Box sx={{ mt: 1, p: 1.25, borderRadius: "8px", background: slate[50], border: `1px solid ${C.lineSoft}` }}>
          <Flx g={1.5} wrap sx={{ alignItems: "flex-end" }}>
            <InputWrapper label="Note (optional)" sx={{ flex: "1 1 220px" }}>
              <TextField size="small" fullWidth value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note governs; UW to correct the loan" />
            </InputWrapper>
            <InputWrapper label="Manual value" sx={{ flex: "0 1 160px" }}>
              <TextField size="small" fullWidth value={manual} onChange={(e) => setManual(e.target.value)} />
            </InputWrapper>
            <Flx ac g={0.75} sx={{ pb: "2px" }}>
              <Button size="small" variant="outlined" color="gray" disabled={!!busy || a.losValue == null} onClick={() => resolve(RESOLUTIONS.LOS)} sx={{ textTransform: "none", borderRadius: "8px" }}>
                {busy === "los" ? "…" : "Keep LOS"}
              </Button>
              <Button size="small" variant="outlined" color="gray" disabled={!!busy || a.documentValue == null} onClick={() => resolve(RESOLUTIONS.DOCUMENT)} sx={{ textTransform: "none", borderRadius: "8px" }}>
                {busy === "document" ? "…" : "Adopt document"}
              </Button>
              <Button size="small" variant="contained" disabled={!!busy || manual === ""} onClick={() => resolve(RESOLUTIONS.MANUAL)} sx={{ textTransform: "none", borderRadius: "8px" }}>
                {busy === "manual" ? "…" : "Set manually"}
              </Button>
            </Flx>
          </Flx>
          {a.status === "doc_only" && (
            <Muted sx={{ display: "block", mt: 0.75, fontSize: 11.5 }}>
              The LOS field is empty — correct the loan, then re-reconcile (or adopt the document value).
            </Muted>
          )}
          {a.status === "los_only" && (
            <Muted sx={{ display: "block", mt: 0.75, fontSize: 11.5 }}>
              No document read yet — upload / extract the source document, or keep the LOS value.
            </Muted>
          )}
        </Box>
      )}
    </Box>
  );
};

const TapeReconcileDrawer = ({ tapeId, row, open, onClose, onChanged }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const dealId = row?.dealId;

  const load = useCallback(async () => {
    if (!tapeId || !dealId) return;
    setLoading(true);
    try {
      setData(await fundingsApi.reconcile(tapeId, dealId));
    } catch (err) {
      console.error("reconcile error:", err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [tapeId, dealId]);

  useEffect(() => {
    if (open) load();
    else setData(null);
  }, [open, load]);

  const onResolved = (res) => {
    // The resolve response is the resolved column; patch it in place and let
    // the parent refresh the rows grid.
    if (res?.field) {
      setData((d) =>
        d
          ? { ...d, columns: (d.columns || []).map((c) => (c.field === res.field ? { ...c, ...res } : c)) }
          : d,
      );
    } else {
      load();
    }
    if (typeof onChanged === "function") onChanged();
  };

  const s = data?.summary || {};
  const href = loanHref(row);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: "min(760px, 100%)", background: C.surface } } }}>
      <Box sx={{ position: "sticky", top: 0, zIndex: 2, px: 2.5, pt: 2.5, pb: 1.75, background: C.surface, borderBottom: `1px solid ${C.line}` }}>
        <Flx jb g={1.5} sx={{ alignItems: "flex-start" }}>
          <Box sx={{ minWidth: 0 }}>
            <Muted sx={{ fontFamily: MONO, fontSize: 11 }}>
              {tapeId} · {row?.originatingLos} · {row?.id}
            </Muted>
            <Txt sx={{ fontSize: 17, fontWeight: 800, color: C.ink, display: "block", lineHeight: 1.2 }}>
              Reconcile {row?.loanNumber != null ? `#${row.loanNumber}` : dealId}
            </Txt>
            <Muted sx={{ display: "block", mt: 0.25 }}>LOS value vs the best document read, exact match — no tolerance.</Muted>
          </Box>
          <Flx ac g={0.5}>
            <Tooltip title="Re-reconcile" arrow>
              <span>
                <IconButton size="small" onClick={load} disabled={loading}>
                  <RefreshRounded sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            {href && (
              <Tooltip title="Open loan" arrow>
                <IconButton size="small" onClick={() => navigate(href)}>
                  <OpenInNewRounded sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={onClose} sx={{ color: C.faint }}>
              <CloseRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </Flx>
        </Flx>
        {data && (
          <Flx ac wrap g={0.5} sx={{ mt: 1.25 }}>
            {["agree", "disagree", "los_only", "doc_only", "no_source", "resolved"].map((k) =>
              s[k] ? <Pill key={k} label={`${agreementMeta(k).label} ${s[k]}`} c={agreementMeta(k).c} bg={agreementMeta(k).bg} size="xs" /> : null,
            )}
          </Flx>
        )}
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 2.5, py: 1 }}>
        {loading && !data ? (
          <Flx center sx={{ py: 6 }}>
            <CircularProgress size={26} />
          </Flx>
        ) : !data ? (
          <Muted>Reconcile unavailable.</Muted>
        ) : (
          <>
            <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr) 110px", columnGap: 1.5, py: 0.75, borderBottom: `1px solid ${C.line}` }}>
              {["Field", "LOS", "Documents", "Status"].map((h) => (
                <Txt key={h} sx={{ fontSize: 10.5, fontWeight: 800, color: C.faint, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: h === "Status" ? "center" : "left" }}>
                  {h}
                </Txt>
              ))}
            </Box>
            {(data.columns || []).map((col) => (
              <ColumnRow key={col.field} col={col} tapeId={tapeId} dealId={dealId} onResolved={onResolved} />
            ))}
          </>
        )}
        <Box sx={{ height: 24 }} />
      </Box>
    </Drawer>
  );
};

export default TapeReconcileDrawer;
